import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import {
  CalendarCheck,
  Wallet,
  TrendingUp,
  ClipboardList,
  LogOut,
  Loader2,
  Plus,
  Trash2,
  Wand2,
  Droplets,
  X,
  Check,
  Archive,
  MessageCircle,
  ExternalLink,
  Pencil,
  Save,
  Power,
} from "lucide-react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Calendar } from "../components/ui/calendar";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useBusinessSettings } from "../lib/businessSettings";
import { useSEO } from "../lib/useSEO";
import api from "../lib/api";
import { buildAdminWsUrl, playChime } from "../lib/portal";
import ChatPanel from "../components/ChatPanel";
import { toast } from "sonner";

const STATUS_FLOW = [
  "pending",
  "confirmed",
  "in_progress",
  "completed",
  "cancelled",
];

const statusColor = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-sky-100 text-sky-800 border-sky-200",
  in_progress: "bg-violet-100 text-violet-800 border-violet-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  useSEO({ titleKey: "seo.admin_title", descriptionKey: "seo.admin_desc" });

  // The active tab is synced to the `?tab=` query param so admin links
  // are deep-linkable (`/admin?tab=messages`), browser-back returns to the
  // previous tab, and refreshes don't dump the user back to "Bookings".
  const VALID_TABS = useMemo(
    () => ["bookings", "services", "customers", "messages", "trash", "settings"],
    [],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "bookings";
  const handleTabChange = (next) => {
    if (next === activeTab) return;
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (next === "bookings") sp.delete("tab");
        else sp.set("tab", next);
        return sp;
      },
      { replace: false },
    );
  };
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    service_slug: "",
    archived: "false",
  });
  const [services, setServices] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [trash, setTrash] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingPurge, setPendingPurge] = useState(null);
  const [pendingEmptyTrash, setPendingEmptyTrash] = useState(false);
  const [blackoutDate, setBlackoutDate] = useState();
  const [blackoutReason, setBlackoutReason] = useState("");
  // Messaging
  const [conversations, setConversations] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [convMessages, setConvMessages] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const selectedConvIdRef = useRef(null);
  const peerTypingTimerRef = useRef(null);
  useEffect(() => {
    selectedConvIdRef.current = selectedConvId;
    setPeerTyping(false);
  }, [selectedConvId]);

  const totalUnread = useMemo(
    () => conversations.reduce((s, c) => s + (c.unread_admin || 0), 0),
    [conversations],
  );

  // Update tab title with unread count
  useEffect(() => {
    const base = "Admin · LocalH2O";
    document.title = totalUnread > 0 ? `(${totalUnread}) New message · LocalH2O` : base;
  }, [totalUnread]);

  const refresh = async () => {
    const [s, b, sv, bl, cu, tr, cv] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/bookings", { params: filters }),
      api.get("/admin/services"),
      api.get("/admin/blackouts"),
      api.get("/admin/customers"),
      api.get("/admin/bookings", { params: { deleted: "true", archived: "all" } }),
      api.get("/admin/conversations"),
    ]);
    setStats(s.data);
    setBookings(b.data);
    setServices(sv.data);
    setBlackouts(bl.data);
    setCustomers(cu.data);
    setTrash(tr.data);
    setConversations(cv.data);
  };

  // Open the chat panel for a conversation: load messages + mark read
  const openConversation = useCallback(async (convId) => {
    setSelectedConvId(convId);
    try {
      const { data } = await api.get(`/admin/conversations/${convId}/messages`);
      setConvMessages(data.messages || []);
      // The GET marks customer messages as read on the server — sync locally.
      setConversations((prev) =>
        prev.map((c) => (c.id === convId ? { ...c, unread_admin: 0 } : c)),
      );
    } catch (e) {
      toast.error("Failed to load conversation");
    }
  }, []);

  // Admin WebSocket — receives new messages + booking events in real time
  useEffect(() => {
    if (!user) return undefined;
    const token = localStorage.getItem("lh2o_admin_token");
    if (!token) return undefined;
    let cancelled = false;
    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(buildAdminWsUrl(token));
      wsRef.current = ws;
      ws.onopen = () => setWsConnected(true);
      ws.onclose = () => {
        setWsConnected(false);
        if (!cancelled) reconnectRef.current = setTimeout(connect, 2500);
      };
      ws.onerror = () => {
        try { ws.close(); } catch (_) { /* ignore */ }
      };
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (evt.type === "new_message") {
            const m = evt.message;
            const convId = evt.conversation_id;
            // Append to currently open chat if it matches
            if (selectedConvIdRef.current === convId) {
              setConvMessages((prev) =>
                prev.find((x) => x.id === m.id) ? prev : [...prev, m],
              );
              // Mark as read immediately if it's a customer message and chat is open
              if (m.sender === "customer") {
                api
                  .get(`/admin/conversations/${convId}/messages`)
                  .catch(() => {});
              }
            }
            // Update conversations list (reorder + unread + preview)
            setConversations((prev) => {
              const idx = prev.findIndex((c) => c.id === convId);
              const isOpen = selectedConvIdRef.current === convId;
              const isCustomer = m.sender === "customer";
              if (idx === -1) {
                // Refresh from server if conversation unknown
                api
                  .get("/admin/conversations")
                  .then((r) => setConversations(r.data))
                  .catch(() => {});
                return prev;
              }
              const updated = {
                ...prev[idx],
                last_message_at: m.created_at,
                last_message_preview: m.body.slice(0, 120),
                unread_admin:
                  isCustomer && !isOpen
                    ? (prev[idx].unread_admin || 0) + 1
                    : prev[idx].unread_admin || 0,
              };
              const next = [updated, ...prev.filter((_, i) => i !== idx)];
              return next;
            });
            if (m.sender === "customer") playChime();
          } else if (evt.type === "typing" && evt.sender === "customer") {
            // Only show typing for the currently open conversation
            if (selectedConvIdRef.current === evt.conversation_id) {
              setPeerTyping(true);
              if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
              peerTypingTimerRef.current = setTimeout(() => setPeerTyping(false), 3000);
            }
          } else if (evt.type === "messages_read" && evt.reader === "customer") {
            // Customer read admin's messages — flip read=true on admin messages of that conv
            if (selectedConvIdRef.current === evt.conversation_id) {
              setConvMessages((prev) =>
                prev.map((m) =>
                  m.sender === "admin" && !m.read ? { ...m, read: true } : m,
                ),
              );
            }
          } else if (evt.type === "conversation_created") {
            setConversations((prev) => {
              if (prev.find((c) => c.id === evt.conversation.id)) return prev;
              return [evt.conversation, ...prev];
            });
          } else if (
            evt.type === "booking_updated" ||
            evt.type === "booking_rescheduled" ||
            evt.type === "booking_cancelled"
          ) {
            // Re-pull bookings list quietly
            api
              .get("/admin/bookings", { params: filters })
              .then((r) => setBookings(r.data))
              .catch(() => {});
          }
        } catch (_) {
          /* ignore */
        }
      };
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const sendAdminMessage = async (body) => {
    if (!selectedConvId) return;
    const { data } = await api.post(
      `/admin/conversations/${selectedConvId}/messages`,
      { body },
    );
    setConvMessages((prev) =>
      prev.find((x) => x.id === data.id) ? prev : [...prev, data],
    );
    setConversations((prev) =>
      prev.map((c) =>
        c.id === selectedConvId
          ? {
              ...c,
              last_message_at: data.created_at,
              last_message_preview: body.slice(0, 120),
            }
          : c,
      ),
    );
  };

  const sendAdminTyping = () => {
    if (!selectedConvId) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(
          JSON.stringify({ type: "typing", conversation_id: selectedConvId }),
        );
      } catch (_) { /* ignore */ }
    }
  };

  useEffect(() => {
    if (user) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filters.status, filters.service_slug, filters.archived]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-sky-600" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status });
      toast.success("Status updated");
      const detail = await api.get(`/admin/bookings/${id}`);
      setSelected(detail.data);
      refresh();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const setArchived = async (id, archived) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}/archive`, {
        archived,
      });
      toast.success(archived ? "Archived" : "Restored");
      setSelected(data);
      refresh();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success(t("admin.deleted_toast"));
      setPendingDelete(null);
      if (selected?.id === id) setSelected(null);
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const restoreBooking = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/restore`);
      toast.success(t("admin.restored_toast"));
      refresh();
    } catch (e) {
      toast.error("Restore failed");
    }
  };

  const confirmPurge = async () => {
    if (!pendingPurge) return;
    const id = pendingPurge.id;
    try {
      await api.delete(`/admin/bookings/${id}/purge`);
      toast.success(t("admin.purged_toast"));
      setPendingPurge(null);
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const confirmEmptyTrash = async () => {
    try {
      await api.delete(`/admin/bookings/trash/empty`);
      toast.success(t("admin.purged_toast"));
      setPendingEmptyTrash(false);
      refresh();
    } catch (e) {
      toast.error("Operation failed");
    }
  };

  const addBlackout = async () => {
    if (!blackoutDate) return;
    const ds = `${blackoutDate.getFullYear()}-${String(blackoutDate.getMonth() + 1).padStart(2, "0")}-${String(blackoutDate.getDate()).padStart(2, "0")}`;
    try {
      await api.post("/admin/blackouts", { date: ds, reason: blackoutReason });
      setBlackoutDate(undefined);
      setBlackoutReason("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const removeBlackout = async (id) => {
    await api.delete(`/admin/blackouts/${id}`);
    refresh();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-sky-600 grid place-items-center shrink-0">
              <Droplets className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold truncate text-sm sm:text-base">
              <span className="text-sky-600">LocalH2O</span>
              <span className="hidden sm:inline"> · {t("admin.dashboard")}</span>
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <div
              className="flex items-center rounded-full border border-slate-200 bg-white p-0.5 text-xs font-semibold"
              data-testid="admin-lang-switcher"
            >
              <button
                onClick={() => setLang("pl")}
                className={`px-2 sm:px-2.5 py-1 rounded-full transition ${lang === "pl" ? "bg-sky-600 text-white" : "text-slate-600"}`}
                data-testid="admin-lang-pl"
              >
                PL
              </button>
              <button
                onClick={() => setLang("en")}
                className={`px-2 sm:px-2.5 py-1 rounded-full transition ${lang === "en" ? "bg-sky-600 text-white" : "text-slate-600"}`}
                data-testid="admin-lang-en"
              >
                EN
              </button>
            </div>
            <span className="text-sm text-slate-500 hidden md:inline">
              {user.email}
            </span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-rose-600"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t("admin.logout")}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-8 py-5 sm:py-8 space-y-5 sm:space-y-6">
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KpiCard
            label={t("admin.kpi_today")}
            value={stats?.bookings_today ?? "—"}
            icon={CalendarCheck}
            color="text-sky-600 bg-sky-50"
            testid="kpi-today"
          />
          <KpiCard
            label={t("admin.kpi_week")}
            value={stats?.bookings_week ?? "—"}
            icon={ClipboardList}
            color="text-violet-600 bg-violet-50"
            testid="kpi-week"
          />
          <KpiCard
            label={t("admin.kpi_revenue")}
            value={
              stats
                ? `${stats.revenue_week.toFixed(0)} ${t("common.currency")}`
                : "—"
            }
            icon={Wallet}
            color="text-emerald-600 bg-emerald-50"
            testid="kpi-revenue"
          />
          <KpiCard
            label={t("admin.kpi_completion")}
            value={stats ? `${stats.completion_rate}%` : "—"}
            icon={TrendingUp}
            color="text-amber-600 bg-amber-50"
            testid="kpi-completion"
          />
        </div>

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          {/* Horizontal scroll container so all 6 tabs are reachable on
              mobile/tablet without overflowing the viewport. The fade
              gradient on the right edge hints there's more to scroll. */}
          <div className="relative -mx-3 sm:mx-0">
            <div className="overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-none px-3 sm:px-0">
              <TabsList
                data-testid="admin-tabs"
                className="inline-flex w-max min-w-full h-auto justify-start gap-1 p-1"
              >
                <TabsTrigger
                  value="bookings"
                  data-testid="tab-bookings"
                  className="snap-start whitespace-nowrap"
                >
                  {t("admin.bookings")}
                </TabsTrigger>
                <TabsTrigger
                  value="services"
                  data-testid="tab-services"
                  className="snap-start whitespace-nowrap"
                >
                  {t("admin.services_tab")}
                </TabsTrigger>
                <TabsTrigger
                  value="customers"
                  data-testid="tab-customers"
                  className="snap-start whitespace-nowrap"
                >
                  {t("admin.customers")}
                </TabsTrigger>
                <TabsTrigger
                  value="messages"
                  data-testid="tab-messages"
                  className="snap-start whitespace-nowrap"
                >
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  {t("admin.messages")}
                  {totalUnread > 0 && (
                    <span
                      className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold"
                      data-testid="messages-unread-count"
                    >
                      {totalUnread}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="trash"
                  data-testid="tab-trash"
                  className="snap-start whitespace-nowrap"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  {t("admin.trash")}
                  {trash.length > 0 && (
                    <span
                      className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold"
                      data-testid="trash-count"
                    >
                      {trash.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="settings"
                  data-testid="tab-settings"
                  className="snap-start whitespace-nowrap"
                >
                  {t("admin.settings")}
                </TabsTrigger>
              </TabsList>
            </div>
            {/* Right-edge fade hint — only visible when overflow exists. */}
            <div
              aria-hidden="true"
              className="lg:hidden pointer-events-none absolute top-0 bottom-0 right-0 w-8 bg-gradient-to-l from-slate-50 via-slate-50/80 to-transparent"
            />
          </div>

          <TabsContent value="bookings" className="mt-6">
            <div className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-5">
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex-1 min-w-[140px] sm:min-w-[180px]">
                  <Label className="text-xs">{t("admin.filter_status")}</Label>
                  <Select
                    value={filters.status || "_all"}
                    onValueChange={(v) =>
                      setFilters({ ...filters, status: v === "_all" ? "" : v })
                    }
                  >
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder={t("admin.all")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">{t("admin.all")}</SelectItem>
                      {STATUS_FLOW.map((s) => (
                        <SelectItem key={s} value={s}>
                          {t(`status.${s}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[140px] sm:min-w-[220px]">
                  <Label className="text-xs">{t("admin.filter_service")}</Label>
                  <Select
                    value={filters.service_slug || "_all"}
                    onValueChange={(v) =>
                      setFilters({
                        ...filters,
                        service_slug: v === "_all" ? "" : v,
                      })
                    }
                  >
                    <SelectTrigger data-testid="filter-service">
                      <SelectValue placeholder={t("admin.all")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">{t("admin.all")}</SelectItem>
                      {services.map((s) => (
                        <SelectItem key={s.slug} value={s.slug}>
                          {s.name_en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end w-full lg:w-auto lg:ml-auto pb-1">
                  <label
                    className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none"
                    data-testid="show-archived-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={filters.archived === "true"}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          archived: e.target.checked ? "true" : "false",
                        })
                      }
                      className="w-4 h-4 accent-sky-600"
                    />
                    {t("admin.show_archived")}
                  </label>
                </div>
              </div>

              {/* Desktop / tablet table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Customer</th>
                      <th className="py-2 pr-3">Services</th>
                      <th className="py-2 pr-3">Total</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody data-testid="bookings-tbody">
                    {bookings.map((b) => (
                      <tr
                        key={b.id}
                        onClick={() => setSelected(b)}
                        className="border-b border-slate-100 hover:bg-sky-50/40 cursor-pointer"
                        data-testid={`booking-row-${b.id}`}
                      >
                        <td className="py-3 pr-3 whitespace-nowrap">
                          <div className="font-medium">{b.scheduled_date}</div>
                          <div className="text-xs text-slate-500">
                            {b.scheduled_time}
                          </div>
                        </td>
                        <td className="py-3 pr-3">
                          <div className="font-medium">{b.customer_name}</div>
                          <div className="text-xs text-slate-500">
                            {b.customer_phone}
                          </div>
                        </td>
                        <td className="py-3 pr-3 max-w-xs">
                          <span className="text-xs text-slate-700">
                            {b.items
                              .map((i) => `${i.quantity}× ${i.name_en}`)
                              .join(", ")}
                          </span>
                        </td>
                        <td className="py-3 pr-3 font-semibold">
                          {b.total.toFixed(0)} {b.currency}
                        </td>
                        <td className="py-3 pr-3">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-md border text-xs font-semibold ${statusColor[b.status]}`}
                          >
                            {t(`status.${b.status}`)}
                          </span>
                          {b.archived && (
                            <span
                              className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-[10px] font-semibold uppercase tracking-wider"
                              data-testid={`archived-badge-${b.id}`}
                            >
                              <Archive className="w-3 h-3" />
                              {t("admin.archived_label")}
                            </span>
                          )}
                        </td>
                        <td
                          className="py-3 pr-3 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setArchived(b.id, !b.archived);
                              }}
                              title={
                                b.archived
                                  ? t("admin.unarchive")
                                  : t("admin.archive")
                              }
                              className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition ${
                                b.archived
                                  ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                  : "border-slate-300 text-slate-600 hover:border-amber-400 hover:bg-amber-50 hover:text-amber-700"
                              }`}
                              data-testid={`row-archive-${b.id}`}
                            >
                              <Archive className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPendingDelete(b);
                              }}
                              title={t("admin.delete")}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-300 text-slate-600 hover:border-rose-400 hover:bg-rose-50 hover:text-rose-700 transition"
                              data-testid={`row-delete-${b.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {bookings.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-slate-400">
                          No bookings
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile card list */}
              <div className="md:hidden space-y-2.5" data-testid="bookings-cards">
                {bookings.map((b) => (
                  <div
                    key={b.id}
                    onClick={() => setSelected(b)}
                    className="rounded-xl border border-slate-200 bg-white p-3 active:bg-sky-50/40"
                    data-testid={`booking-card-${b.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-slate-500">
                          {b.scheduled_date} · {b.scheduled_time}
                        </div>
                        <div className="font-semibold text-slate-900 mt-0.5 truncate">
                          {b.customer_name}
                        </div>
                        <div className="text-xs text-slate-500">
                          {b.customer_phone}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display font-bold text-sky-700">
                          {b.total.toFixed(0)} {b.currency}
                        </div>
                        <span
                          className={`inline-block mt-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold ${statusColor[b.status]}`}
                        >
                          {t(`status.${b.status}`)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-slate-600 truncate">
                      {b.items
                        .map((i) => `${i.quantity}× ${i.name_en}`)
                        .join(", ")}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      {b.archived ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-[10px] font-semibold uppercase tracking-wider"
                          data-testid={`archived-badge-${b.id}`}
                        >
                          <Archive className="w-3 h-3" />
                          {t("admin.archived_label")}
                        </span>
                      ) : (
                        <span />
                      )}
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setArchived(b.id, !b.archived);
                          }}
                          className={`inline-flex items-center gap-1 text-xs font-semibold rounded-lg border px-2 py-1 ${
                            b.archived
                              ? "border-amber-300 bg-amber-50 text-amber-700"
                              : "border-slate-300 text-slate-600"
                          }`}
                          data-testid={`mobile-archive-${b.id}`}
                        >
                          <Archive className="w-3.5 h-3.5" />
                          {b.archived
                            ? t("admin.unarchive")
                            : t("admin.archive")}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDelete(b);
                          }}
                          className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg border border-rose-300 text-rose-700 bg-rose-50 px-2 py-1"
                          data-testid={`mobile-delete-${b.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {t("admin.delete")}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {bookings.length === 0 && (
                  <div className="py-12 text-center text-slate-400 text-sm">
                    No bookings
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="services" className="mt-6">
            <ServicesPanel
              services={services}
              onChanged={refresh}
            />
          </TabsContent>

          <TabsContent value="customers" className="mt-6">
            <div className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-5">
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Phone</th>
                      <th className="py-2 pr-3">Bookings</th>
                      <th className="py-2 pr-3">Spent</th>
                      <th className="py-2 pr-3">Last</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => (
                      <tr key={c.email} className="border-b border-slate-100">
                        <td className="py-2 pr-3 font-medium">{c.name}</td>
                        <td className="py-2 pr-3">{c.email}</td>
                        <td className="py-2 pr-3">{c.phone}</td>
                      <td className="py-2 pr-3">{c.bookings}</td>
                      <td className="py-2 pr-3 font-semibold">
                        {c.spent.toFixed(0)} PLN
                      </td>
                      <td className="py-2 pr-3 text-slate-500">
                        {c.last_booking}
                      </td>
                    </tr>
                  ))}
                    {customers.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-12 text-center text-slate-400"
                        >
                          No customers yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile card list */}
              <div className="sm:hidden space-y-2.5">
                {customers.map((c) => (
                  <div
                    key={c.email}
                    className="rounded-xl border border-slate-200 bg-white p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate">{c.name}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {c.email}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {c.phone}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display font-bold text-sky-700">
                          {c.spent.toFixed(0)} PLN
                        </div>
                        <div className="text-xs text-slate-500">
                          {c.bookings} bookings
                        </div>
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-2">
                      Last: {c.last_booking}
                    </div>
                  </div>
                ))}
                {customers.length === 0 && (
                  <div className="py-12 text-center text-slate-400 text-sm">
                    No customers yet
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <MessagesPanel
              conversations={conversations}
              setConversations={setConversations}
              selectedConvId={selectedConvId}
              setSelectedConvId={setSelectedConvId}
              openConversation={openConversation}
              messages={convMessages}
              onSend={sendAdminMessage}
              onTyping={sendAdminTyping}
              peerTyping={peerTyping}
              wsConnected={wsConnected}
              setSelectedBooking={(id) => {
                if (!id) return;
                api
                  .get(`/admin/bookings/${id}`)
                  .then((r) => setSelected(r.data))
                  .catch(() => toast.error("Booking not found"));
              }}
            />
          </TabsContent>

          <TabsContent value="trash" className="mt-6">
            <div className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-5">
              {trash.length > 0 && (
                <div className="flex justify-end mb-3">
                  <button
                    onClick={() => setPendingEmptyTrash(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-rose-50 text-rose-700 px-3 py-1.5 text-xs font-semibold hover:bg-rose-100"
                    data-testid="empty-trash-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> {t("admin.empty_trash")}
                  </button>
                </div>
              )}
              {trash.length === 0 ? (
                <div
                  className="py-12 text-center text-slate-400 text-sm"
                  data-testid="trash-empty"
                >
                  {t("admin.trash_empty")}
                </div>
              ) : (
                <div className="space-y-2.5" data-testid="trash-list">
                  {trash.map((b) => (
                    <div
                      key={b.id}
                      className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 sm:p-4"
                      data-testid={`trash-row-${b.id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-slate-500">
                            {b.scheduled_date} · {b.scheduled_time}
                          </div>
                          <div className="font-semibold text-slate-900 mt-0.5">
                            {b.customer_name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {b.customer_phone} · {b.customer_email}
                          </div>
                          <div className="mt-1.5 text-xs text-slate-700">
                            {b.items
                              .map((i) => `${i.quantity}× ${i.name_en}`)
                              .join(", ")}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-display font-bold text-sky-700">
                            {b.total.toFixed(0)} {b.currency}
                          </div>
                          {b.deleted_at && (
                            <div className="text-[10px] text-slate-400 mt-1">
                              {new Date(b.deleted_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-end gap-2">
                        <button
                          onClick={() => restoreBooking(b.id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 px-2.5 py-1 text-xs font-semibold hover:bg-emerald-100"
                          data-testid={`trash-restore-${b.id}`}
                        >
                          <Check className="w-3.5 h-3.5" /> {t("admin.restore")}
                        </button>
                        <button
                          onClick={() => setPendingPurge(b)}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-300 bg-rose-50 text-rose-700 px-2.5 py-1 text-xs font-semibold hover:bg-rose-100"
                          data-testid={`trash-purge-${b.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> {t("admin.purge")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-6 space-y-6">
            <BusinessContactPanel />
            <ReviewsPanel />
            <ChangePasswordPanel />
            <div className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-5">
              <h3 className="font-display text-lg font-semibold">
                {t("admin.blackouts")}
              </h3>
              <div className="mt-4 grid lg:grid-cols-2 gap-6">
                <div className="rounded-xl border border-slate-200 p-2 sm:p-3 bg-white">
                  <Calendar
                    mode="single"
                    selected={blackoutDate}
                    onSelect={setBlackoutDate}
                    data-testid="blackout-calendar"
                  />
                </div>
                <div>
                  <Label>{t("admin.reason")}</Label>
                  <Input
                    value={blackoutReason}
                    onChange={(e) => setBlackoutReason(e.target.value)}
                    placeholder="Holiday / Maintenance"
                    data-testid="blackout-reason"
                  />
                  <button
                    onClick={addBlackout}
                    disabled={!blackoutDate}
                    className="mt-3 btn-primary inline-flex items-center gap-1.5 disabled:opacity-40"
                    data-testid="add-blackout-btn"
                  >
                    <Plus className="w-4 h-4" /> {t("admin.add")}
                  </button>

                  <div className="mt-6 space-y-2">
                    {blackouts.map((b) => (
                      <div
                        key={b.id}
                        className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      >
                        <div>
                          <span className="font-semibold">{b.date}</span>
                          {b.reason && (
                            <span className="ml-2 text-slate-500 text-xs">
                              · {b.reason}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => removeBlackout(b.id)}
                          className="text-rose-600 hover:bg-rose-50 p-1.5 rounded"
                          data-testid={`remove-blackout-${b.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {blackouts.length === 0 && (
                      <div className="text-xs text-slate-400">
                        No blackout dates set.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Booking Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          className="max-w-2xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6"
          data-testid="booking-detail-dialog"
        >
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  Booking #{selected.id.slice(0, 8).toUpperCase()}
                </DialogTitle>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 text-sm mt-4">
                <Field label="Customer" value={selected.customer_name} />
                <Field label="Phone" value={selected.customer_phone} />
                <Field label="Email" value={selected.customer_email} />
                <Field
                  label="Address"
                  value={`${selected.address_street}, ${selected.address_postal} ${selected.address_city}`}
                />
                <Field
                  label="When"
                  value={`${selected.scheduled_date} · ${selected.scheduled_time}`}
                />
                <Field
                  label="Total"
                  value={`${selected.total.toFixed(0)} ${selected.currency}`}
                />
                <Field
                  label="Payment"
                  value={
                    selected.payment_method === "cash"
                      ? "Cash"
                      : "Bank transfer"
                  }
                />
                <Field
                  label="Outdoor taps"
                  value={`${selected.has_outdoor_cold_tap ? "Cold ✓" : "Cold ✗"} · ${selected.has_outdoor_hot_tap ? "Hot ✓" : "Hot ✗"}`}
                />
              </div>

              <div className="mt-5">
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                  {t("admin.services_tab")}
                </div>
                <div
                  className="rounded-xl border border-slate-200 overflow-hidden"
                  data-testid="booking-line-items"
                >
                  {selected.items.map((it, idx) => {
                    const svc = services.find((s) => s.slug === it.slug);
                    const isM2 = svc?.pricing_mode === "per_m2";
                    const unit = isM2
                      ? "m²"
                      : it.quantity === 1
                        ? t("booking.bin_unit_singular")
                        : t("booking.bin_unit_plural");
                    return (
                      <div
                        key={it.slug || `item-${idx}`}
                        className={`flex items-center justify-between px-4 py-3 text-sm ${
                          idx > 0 ? "border-t border-slate-200" : ""
                        }`}
                        data-testid={`item-row-${it.slug}`}
                      >
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900">
                            {lang === "pl" ? it.name_pl : it.name_en}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {it.quantity} {unit} × {it.unit_price.toFixed(0)}{" "}
                            {selected.currency}
                          </div>
                        </div>
                        <div className="font-display font-bold text-slate-900 ml-4 whitespace-nowrap">
                          {it.subtotal.toFixed(0)} {selected.currency}
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between px-4 py-3 text-sm bg-sky-50 border-t border-sky-200">
                    <span className="text-slate-600 font-semibold">
                      {t("booking.total")}
                    </span>
                    <span className="font-display text-lg font-bold text-sky-700">
                      {selected.total.toFixed(0)} {selected.currency}
                    </span>
                  </div>
                </div>
              </div>
              {selected.notes && (
                <div className="mt-3 text-sm">
                  <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
                    Notes
                  </div>
                  <div className="text-slate-700">{selected.notes}</div>
                </div>
              )}

              <div className="mt-5">
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                  {t("admin.timeline")}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {STATUS_FLOW.map((s, i) => {
                    const reached = selected.status_history?.some(
                      (h) => h.status === s,
                    );
                    const current = selected.status === s;
                    return (
                      <button
                        key={s}
                        onClick={() => updateStatus(selected.id, s)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                          current
                            ? "bg-sky-600 border-sky-600 text-white"
                            : reached
                              ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                              : "bg-white border-slate-300 text-slate-600 hover:border-sky-400"
                        }`}
                        data-testid={`set-status-${s}`}
                      >
                        {reached && !current && <Check className="w-3 h-3" />}
                        {t(`status.${s}`)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  onClick={() => setPendingDelete(selected)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-rose-50 text-rose-700 px-3 py-2 text-xs font-semibold hover:bg-rose-100 transition"
                  data-testid="dialog-delete-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t("admin.delete")}
                </button>
                <button
                  onClick={() => setArchived(selected.id, !selected.archived)}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                    selected.archived
                      ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                      : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"
                  }`}
                  data-testid="archive-toggle-btn"
                >
                  <Archive className="w-3.5 h-3.5" />
                  {selected.archived
                    ? t("admin.unarchive")
                    : t("admin.archive")}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.delete_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.delete_confirm_body")}
              {pendingDelete && (
                <span className="block mt-2 font-mono text-xs text-slate-500">
                  #{pendingDelete.id.slice(0, 8).toUpperCase()} ·{" "}
                  {pendingDelete.customer_name} ·{" "}
                  {pendingDelete.scheduled_date}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">
              {t("admin.delete_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="delete-confirm-btn"
            >
              {t("admin.delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingPurge}
        onOpenChange={(o) => !o && setPendingPurge(null)}
      >
        <AlertDialogContent data-testid="purge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.purge_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.purge_confirm_body")}
              {pendingPurge && (
                <span className="block mt-2 font-mono text-xs text-slate-500">
                  #{pendingPurge.id.slice(0, 8).toUpperCase()} ·{" "}
                  {pendingPurge.customer_name} ·{" "}
                  {pendingPurge.scheduled_date}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purge-cancel-btn">
              {t("admin.delete_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmPurge}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="purge-confirm-btn"
            >
              {t("admin.purge_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingEmptyTrash}
        onOpenChange={(o) => !o && setPendingEmptyTrash(false)}
      >
        <AlertDialogContent data-testid="empty-trash-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.empty_trash_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.empty_trash_confirm_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("admin.delete_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmEmptyTrash}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="empty-trash-confirm-btn"
            >
              {t("admin.empty_trash")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-5"
      data-testid={testid}
    >
      <div
        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl grid place-items-center ${color}`}
      >
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      </div>
      <div className="mt-2 sm:mt-3 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500 leading-tight">
        {label}
      </div>
      <div className="mt-1 font-display text-xl sm:text-3xl font-bold text-slate-900">
        {value}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className="text-sm text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}

function MessagesPanel({
  conversations,
  setConversations,
  selectedConvId,
  setSelectedConvId,
  openConversation,
  messages,
  onSend,
  onTyping,
  peerTyping,
  wsConnected,
  setSelectedBooking,
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [pendingDeleteConv, setPendingDeleteConv] = useState(null);

  const refreshList = useCallback(async () => {
    try {
      const params = showArchived ? { archived: "all" } : {};
      const { data } = await api.get("/admin/conversations", { params });
      setConversations(data);
    } catch (_) {
      /* ignore */
    }
  }, [showArchived, setConversations]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  const filtered = conversations.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.customer_name || "").toLowerCase().includes(q) ||
      (c.customer_phone || "").toLowerCase().includes(q) ||
      (c.customer_email || "").toLowerCase().includes(q) ||
      (c.reference || "").toLowerCase().includes(q)
    );
  });

  const selected = conversations.find((c) => c.id === selectedConvId);
  const isAnonymous = (c) =>
    c?.kind === "anonymous" || !c?.booking_id || !c?.reference;

  const handleArchiveToggle = async (conv, e) => {
    e?.stopPropagation();
    try {
      const next = !conv.archived;
      await api.patch(`/admin/conversations/${conv.id}/archive`, {
        archived: next,
      });
      toast.success(
        next ? t("admin.messages_archived_toast") : t("admin.messages_unarchived_toast"),
      );
      refreshList();
    } catch (_) {
      toast.error("Action failed");
    }
  };

  const handleDelete = async () => {
    if (!pendingDeleteConv) return;
    const id = pendingDeleteConv.id;
    try {
      await api.delete(`/admin/conversations/${id}`);
      toast.success(t("admin.messages_deleted_toast"));
      setPendingDeleteConv(null);
      if (selectedConvId === id) setSelectedConvId(null);
      refreshList();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 overflow-hidden"
      data-testid="admin-messages-panel"
    >
      <div className="grid md:grid-cols-[300px_1fr]">
        {/* Conversation list */}
        <div className="border-b md:border-b-0 md:border-r border-slate-200 max-h-[70vh] overflow-y-auto">
          <div className="p-3 border-b border-slate-200 sticky top-0 bg-white z-10 space-y-2">
            <Input
              placeholder={t("admin.messages_search_placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-conv-search"
            />
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="rounded border-slate-300"
                data-testid="admin-conv-show-archived"
              />
              {t("admin.messages_show_archived")}
            </label>
          </div>
          {filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400" data-testid="admin-conv-empty">
              {t("admin.messages_empty")}
            </div>
          )}
          {filtered.map((c) => (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              onClick={() => openConversation(c.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openConversation(c.id);
                }
              }}
              className={`group relative w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-sky-50/50 transition cursor-pointer ${
                selectedConvId === c.id ? "bg-sky-50" : ""
              } ${c.archived ? "opacity-70" : ""}`}
              data-testid={`admin-conv-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                    {c.customer_name || c.customer_email || "Visitor"}
                    {isAnonymous(c) && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0"
                        data-testid={`admin-conv-visitor-${c.id}`}
                      >
                        {t("admin.messages_visitor_badge")}
                      </span>
                    )}
                    {c.archived && (
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-700 rounded-full px-1.5 py-0">
                        {t("admin.messages_archived_badge")}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono truncate">
                    {c.reference || c.customer_email || c.customer_phone || "—"}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">
                    {c.last_message_preview || "—"}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  {c.unread_admin > 0 && (
                    <span
                      className="inline-block px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold"
                      data-testid={`admin-conv-unread-${c.id}`}
                    >
                      {c.unread_admin}
                    </span>
                  )}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                    <button
                      type="button"
                      onClick={(e) => handleArchiveToggle(c, e)}
                      title={
                        c.archived
                          ? t("admin.messages_unarchive")
                          : t("admin.messages_archive")
                      }
                      className="p-1 rounded hover:bg-white text-slate-500 hover:text-sky-700"
                      data-testid={`admin-conv-archive-${c.id}`}
                    >
                      <Archive className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDeleteConv(c);
                      }}
                      title={t("admin.messages_delete")}
                      className="p-1 rounded hover:bg-white text-slate-500 hover:text-rose-700"
                      data-testid={`admin-conv-delete-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Chat area */}
        <div className="p-3 sm:p-4 min-h-[500px]">
          {!selected ? (
            <div className="h-full grid place-items-center text-center text-sm text-slate-400 py-16" data-testid="admin-conv-placeholder">
              <div>
                <MessageCircle className="w-10 h-10 mx-auto opacity-50 mb-3" />
                <div className="font-semibold text-slate-600">
                  {t("admin.messages_select")}
                </div>
                <div className="mt-1">{t("admin.messages_select_hint")}</div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <div>
                  <div className="font-semibold text-sm">
                    {selected.customer_name || selected.customer_email || "Visitor"}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {[selected.customer_email, selected.customer_phone]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!isAnonymous(selected) && selected.booking_id && (
                    <button
                      onClick={() => setSelectedBooking(selected.booking_id)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900"
                      data-testid="admin-conv-open-booking"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      {t("admin.messages_open_booking")} · {selected.reference}
                    </button>
                  )}
                  <button
                    onClick={(e) => handleArchiveToggle(selected, e)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-sky-700"
                    data-testid="admin-conv-archive-active"
                  >
                    <Archive className="w-3.5 h-3.5" />
                    {selected.archived
                      ? t("admin.messages_unarchive")
                      : t("admin.messages_archive")}
                  </button>
                  <button
                    onClick={() => setPendingDeleteConv(selected)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-800"
                    data-testid="admin-conv-delete-active"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {t("admin.messages_delete")}
                  </button>
                </div>
              </div>
              <ChatPanel
                messages={messages}
                onSend={onSend}
                onTyping={onTyping}
                currentSender="admin"
                peerTyping={peerTyping}
                wsConnected={wsConnected}
                peerName={selected.customer_name || selected.customer_email || "Visitor"}
                emptyKey="portal.empty_chat"
                placeholderKey="admin.messages_typing_placeholder"
                sendKey="admin.messages_send"
                testidPrefix="admin-chat"
              />
            </div>
          )}
        </div>
      </div>

      <AlertDialog
        open={!!pendingDeleteConv}
        onOpenChange={(o) => !o && setPendingDeleteConv(null)}
      >
        <AlertDialogContent data-testid="admin-conv-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.messages_delete_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.messages_delete_confirm_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-conv-delete-cancel">
              {t("admin.delete_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="admin-conv-delete-confirm"
            >
              {t("admin.messages_delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// ----------------------------------------------------------------------------
// Services CRUD — inline editor for name/price/features
// ----------------------------------------------------------------------------
function ServicesPanel({ services, onChanged }) {
  const { t } = useI18n();
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const startEdit = (svc) => {
    setEditingId(svc.id);
    setDraft({
      slug: svc.slug,
      name_en: svc.name_en,
      name_pl: svc.name_pl,
      description_en: svc.description_en,
      description_pl: svc.description_pl,
      base_price: svc.base_price,
      unit_en: svc.unit_en,
      unit_pl: svc.unit_pl,
      duration_min: svc.duration_min,
      active: svc.active !== false,
      features_en: (svc.features_en || []).join("\n"),
      features_pl: (svc.features_pl || []).join("\n"),
      _isNew: false,
    });
  };

  const startCreate = () => {
    setEditingId("__new");
    setDraft({
      slug: "",
      name_en: "",
      name_pl: "",
      description_en: "",
      description_pl: "",
      base_price: 0,
      unit_en: "service",
      unit_pl: "usługa",
      duration_min: 60,
      active: true,
      features_en: "",
      features_pl: "",
      _isNew: true,
    });
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(null);
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.slug.trim() || !draft.name_en.trim() || !draft.name_pl.trim()) {
      toast.error(t("errors.required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: draft.slug.trim(),
        name_en: draft.name_en.trim(),
        name_pl: draft.name_pl.trim(),
        description_en: draft.description_en.trim(),
        description_pl: draft.description_pl.trim(),
        base_price: Number(draft.base_price) || 0,
        unit_en: draft.unit_en.trim() || "service",
        unit_pl: draft.unit_pl.trim() || "usługa",
        duration_min: Number(draft.duration_min) || 60,
        active: !!draft.active,
        features_en: draft.features_en
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        features_pl: draft.features_pl
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (draft._isNew) {
        await api.post("/admin/services", payload);
        toast.success(t("admin.services_created"));
      } else {
        await api.patch(`/admin/services/${editingId}`, payload);
        toast.success(t("admin.services_saved"));
      }
      cancel();
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/services/${pendingDelete.id}`);
      toast.success(t("admin.services_deleted"));
      setPendingDelete(null);
      await onChanged();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-5" data-testid="admin-services-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-500">
          {services.length} {services.length === 1 ? "service" : "services"}
        </div>
        <button
          onClick={startCreate}
          disabled={editingId !== null}
          className="btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
          data-testid="admin-services-add-btn"
        >
          <Plus className="w-4 h-4" />
          {t("admin.services_add")}
        </button>
      </div>

      <div className="space-y-3">
        {editingId === "__new" && (
          <ServiceEditor
            draft={draft}
            setDraft={setDraft}
            onSave={save}
            onCancel={cancel}
            saving={saving}
          />
        )}

        {services.map((s) =>
          editingId === s.id ? (
            <ServiceEditor
              key={s.id}
              draft={draft}
              setDraft={setDraft}
              onSave={save}
              onCancel={cancel}
              saving={saving}
            />
          ) : (
            <ServiceCard
              key={s.id}
              service={s}
              disabled={editingId !== null}
              onEdit={() => startEdit(s)}
              onDelete={() => setPendingDelete(s)}
            />
          ),
        )}

        {services.length === 0 && editingId !== "__new" && (
          <div className="py-12 text-center text-slate-400 text-sm">
            No services yet — click "{t("admin.services_add")}" to create one.
          </div>
        )}
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent data-testid="admin-services-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.services_delete_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.services_delete_confirm_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-services-delete-cancel">
              {t("admin.services_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="admin-services-delete-confirm"
            >
              {t("admin.services_delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ServiceCard({ service, disabled, onEdit, onDelete }) {
  const { t } = useI18n();
  return (
    <div
      className="rounded-xl border border-slate-200 p-3 sm:p-4"
      data-testid={`admin-service-${service.slug}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-display font-semibold truncate text-base">
              {service.name_en}
            </div>
            <span
              className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold ${
                service.active !== false
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-slate-100 text-slate-500 border border-slate-200"
              }`}
            >
              {service.active !== false
                ? t("admin.services_active")
                : t("admin.services_inactive")}
            </span>
          </div>
          <div className="text-xs text-slate-500 truncate">
            {service.name_pl} · /{service.slug} · {service.duration_min} min
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-base sm:text-lg font-bold text-sky-700 whitespace-nowrap">
            {service.base_price.toFixed(0)} PLN
          </div>
          <div className="text-[11px] text-slate-500">{service.unit_en}</div>
        </div>
      </div>

      {(service.features_en?.length || 0) > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {service.features_en.slice(0, 4).map((f, i) => (
            <span
              key={`${service.slug}-feat-${i}`}
              className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100"
            >
              {f}
            </span>
          ))}
          {service.features_en.length > 4 && (
            <span className="text-[11px] text-slate-400">
              +{service.features_en.length - 4}
            </span>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          onClick={onEdit}
          disabled={disabled}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:text-sky-700 disabled:opacity-40"
          data-testid={`admin-service-edit-${service.slug}`}
        >
          <Pencil className="w-3.5 h-3.5" />
          {t("admin.services_edit")}
        </button>
        <button
          onClick={onDelete}
          disabled={disabled}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-rose-600 hover:text-rose-800 disabled:opacity-40"
          data-testid={`admin-service-delete-${service.slug}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
          {t("admin.services_delete")}
        </button>
      </div>
    </div>
  );
}

function ServiceEditor({ draft, setDraft, onSave, onCancel, saving }) {
  const { t } = useI18n();
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  return (
    <div
      className="rounded-xl border-2 border-sky-200 bg-sky-50/30 p-4"
      data-testid="admin-service-editor"
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-xs">{t("admin.services_slug")}</Label>
            <Input
              value={draft.slug}
              onChange={(e) =>
                set(
                  "slug",
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, "-")
                    .replace(/-+/g, "-"),
                )
              }
              disabled={!draft._isNew}
              placeholder="driveway-pressure-washing"
              className="font-mono"
              data-testid="admin-service-slug-input"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer pb-2">
            <input
              type="checkbox"
              checked={!!draft.active}
              onChange={(e) => set("active", e.target.checked)}
              className="w-4 h-4 accent-sky-600"
              data-testid="admin-service-active-toggle"
            />
            <Power className="w-3.5 h-3.5" />
            {draft.active
              ? t("admin.services_active")
              : t("admin.services_inactive")}
          </label>
        </div>

        <div>
          <Label className="text-xs">{t("admin.services_name_en")}</Label>
          <Input
            value={draft.name_en}
            onChange={(e) => set("name_en", e.target.value)}
            data-testid="admin-service-name-en-input"
          />
        </div>
        <div>
          <Label className="text-xs">{t("admin.services_name_pl")}</Label>
          <Input
            value={draft.name_pl}
            onChange={(e) => set("name_pl", e.target.value)}
            data-testid="admin-service-name-pl-input"
          />
        </div>

        <div>
          <Label className="text-xs">{t("admin.services_price")}</Label>
          <Input
            type="number"
            min={0}
            step="0.5"
            value={draft.base_price}
            onChange={(e) => set("base_price", e.target.value)}
            data-testid="admin-service-price-input"
          />
        </div>
        <div>
          <Label className="text-xs">{t("admin.services_duration")}</Label>
          <Input
            type="number"
            min={5}
            step="5"
            value={draft.duration_min}
            onChange={(e) => set("duration_min", e.target.value)}
            data-testid="admin-service-duration-input"
          />
        </div>

        <div>
          <Label className="text-xs">{t("admin.services_unit_en")}</Label>
          <Input
            value={draft.unit_en}
            onChange={(e) => set("unit_en", e.target.value)}
            placeholder="per m² / per bin"
            data-testid="admin-service-unit-en-input"
          />
        </div>
        <div>
          <Label className="text-xs">{t("admin.services_unit_pl")}</Label>
          <Input
            value={draft.unit_pl}
            onChange={(e) => set("unit_pl", e.target.value)}
            placeholder="za m² / za kubeł"
            data-testid="admin-service-unit-pl-input"
          />
        </div>

        <div className="sm:col-span-2">
          <Label className="text-xs">{t("admin.services_desc_en")}</Label>
          <textarea
            value={draft.description_en}
            onChange={(e) => set("description_en", e.target.value)}
            rows={3}
            className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="admin-service-desc-en-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">{t("admin.services_desc_pl")}</Label>
          <textarea
            value={draft.description_pl}
            onChange={(e) => set("description_pl", e.target.value)}
            rows={3}
            className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="admin-service-desc-pl-input"
          />
        </div>

        <div>
          <Label className="text-xs">{t("admin.services_features_en")}</Label>
          <textarea
            value={draft.features_en}
            onChange={(e) => set("features_en", e.target.value)}
            rows={4}
            placeholder={"Eco-friendly detergents\nMoss & algae removal"}
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm font-mono"
            data-testid="admin-service-features-en-input"
          />
        </div>
        <div>
          <Label className="text-xs">{t("admin.services_features_pl")}</Label>
          <textarea
            value={draft.features_pl}
            onChange={(e) => set("features_pl", e.target.value)}
            rows={4}
            placeholder={"Ekologiczne detergenty\nUsuwanie mchu i glonów"}
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm font-mono"
            data-testid="admin-service-features-pl-input"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 disabled:opacity-50"
          data-testid="admin-service-editor-cancel"
        >
          {t("admin.services_cancel")}
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
          data-testid="admin-service-editor-save"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {t("admin.services_save")}
        </button>
      </div>
    </div>
  );
}


function ChangePasswordPanel() {
  const { t } = useI18n();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error(t("admin.password_min_8"));
      return;
    }
    if (next !== confirm) {
      toast.error(t("admin.passwords_do_not_match"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(t("admin.password_changed"));
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-5"
      data-testid="change-password-panel"
    >
      <h3 className="font-display text-lg font-semibold">
        {t("admin.change_password")}
      </h3>
      <p className="text-sm text-slate-500 mt-1">
        {t("admin.change_password_subtitle")}
      </p>
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        <div>
          <Label>{t("admin.current_password")}</Label>
          <Input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            data-testid="change-pwd-current"
          />
        </div>
        <div>
          <Label>{t("admin.new_password")}</Label>
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            data-testid="change-pwd-new"
          />
        </div>
        <div>
          <Label>{t("admin.confirm_new_password")}</Label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            data-testid="change-pwd-confirm"
          />
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <button
          type="submit"
          disabled={busy || !current || !next || !confirm}
          className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="change-pwd-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {t("admin.change_password")}
        </button>
      </div>
    </form>
  );
}

function BusinessContactPanel() {
  const { t } = useI18n();
  const { settings, applyUpdate, refresh } = useBusinessSettings();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [addrPl, setAddrPl] = useState("");
  const [addrEn, setAddrEn] = useState("");
  const [baseline, setBaseline] = useState("0");
  const [busy, setBusy] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate the form from the live settings the first time they arrive,
  // and any time the user resets via "Discard changes".
  useEffect(() => {
    if (!hydrated && settings && (settings.email || settings.phone)) {
      setEmail(settings.email || "");
      setPhone(settings.phone || "");
      setAddrPl(settings.address_pl || "");
      setAddrEn(settings.address_en || "");
      setBaseline(String(settings.reviews_count_baseline ?? 0));
      setHydrated(true);
    }
  }, [settings, hydrated]);

  const dirty =
    hydrated &&
    (email !== (settings.email || "") ||
      phone !== (settings.phone || "") ||
      addrPl !== (settings.address_pl || "") ||
      addrEn !== (settings.address_en || "") ||
      Number(baseline || 0) !== Number(settings.reviews_count_baseline ?? 0));

  const reset = () => {
    setEmail(settings.email || "");
    setPhone(settings.phone || "");
    setAddrPl(settings.address_pl || "");
    setAddrEn(settings.address_en || "");
    setBaseline(String(settings.reviews_count_baseline ?? 0));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/business", {
        email: email.trim(),
        phone: phone.trim(),
        address_pl: addrPl.trim(),
        address_en: addrEn.trim(),
        reviews_count_baseline: Math.max(0, Number(baseline) || 0),
      });
      applyUpdate(data);
      toast.success(t("admin.contact_saved"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.contact_save_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-5"
      data-testid="business-contact-panel"
    >
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-lg font-semibold">
            {t("admin.contact_title")}
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md">
            {t("admin.contact_subtitle")}
          </p>
        </div>
        {settings.updated_at && (
          <span className="text-[11px] text-slate-400">
            {t("admin.contact_last_saved")}{" "}
            {new Date(settings.updated_at).toLocaleString()}
          </span>
        )}
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="biz-email">{t("admin.contact_email_label")}</Label>
          <Input
            id="biz-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="hello@localh2o.com"
            data-testid="biz-email-input"
          />
        </div>
        <div>
          <Label htmlFor="biz-phone">{t("admin.contact_phone_label")}</Label>
          <Input
            id="biz-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+48 881 476 730"
            data-testid="biz-phone-input"
          />
        </div>
        <div>
          <Label htmlFor="biz-addr-pl">{t("admin.contact_address_pl_label")}</Label>
          <Input
            id="biz-addr-pl"
            value={addrPl}
            onChange={(e) => setAddrPl(e.target.value)}
            placeholder="Gdańsk, Polska"
            data-testid="biz-addr-pl-input"
          />
        </div>
        <div>
          <Label htmlFor="biz-addr-en">{t("admin.contact_address_en_label")}</Label>
          <Input
            id="biz-addr-en"
            value={addrEn}
            onChange={(e) => setAddrEn(e.target.value)}
            placeholder="Gdańsk, Poland"
            data-testid="biz-addr-en-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="biz-baseline">
            {t("admin.contact_reviews_baseline_label")}
          </Label>
          <Input
            id="biz-baseline"
            type="number"
            min="0"
            value={baseline}
            onChange={(e) => setBaseline(e.target.value)}
            placeholder="0"
            data-testid="biz-baseline-input"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            {t("admin.contact_reviews_baseline_help")}
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={reset}
          disabled={!dirty || busy}
          className="px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          data-testid="biz-discard-btn"
        >
          {t("admin.contact_discard")}
        </button>
        <button
          type="submit"
          disabled={!dirty || busy}
          className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="biz-save-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {t("admin.contact_save")}
        </button>
      </div>
    </form>
  );
}

function ReviewsPanel() {
  const { t } = useI18n();
  const [reviews, setReviews] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [count, setCount] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reviews");
      setReviews(Array.isArray(data?.items) ? data.items : []);
      setTotalCount(Number(data?.total_count || 0));
    } catch (_) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/reviews/generate", {
        count: Math.max(1, Math.min(20, Number(count) || 1)),
      });
      toast.success(
        t("admin.reviews_generated_toast").replace(
          "{n}",
          (data?.created || []).length,
        ),
      );
      refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || t("admin.reviews_generate_failed"),
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success(t("admin.reviews_deleted_toast"));
      refresh();
    } catch (_) {
      toast.error(t("admin.reviews_delete_failed"));
    }
  };

  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-5"
      data-testid="admin-reviews-panel"
    >
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-lg font-semibold">
            {t("admin.reviews_title")}
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md">
            {t("admin.reviews_subtitle")}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-slate-500">
            {t("admin.reviews_total_label")}
          </div>
          <div
            className="font-display text-2xl font-bold text-sky-700"
            data-testid="admin-reviews-total"
          >
            {totalCount}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="rev-count">
            {t("admin.reviews_generate_count_label")}
          </Label>
          <Input
            id="rev-count"
            type="number"
            min="1"
            max="20"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="w-24"
            data-testid="rev-count-input"
          />
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="rev-generate-btn"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Wand2 className="w-4 h-4" />
          )}
          {t("admin.reviews_generate_btn")}
        </button>
        <p className="text-[11px] text-slate-400 max-w-xs">
          {t("admin.reviews_generate_help")}
        </p>
      </div>

      <div className="mt-5">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          {t("admin.reviews_list_label")} ({reviews.length})
        </div>
        {loading ? (
          <div className="text-sm text-slate-400">…</div>
        ) : reviews.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 text-center">
            {t("admin.reviews_empty")}
          </div>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {reviews.map((rv) => (
              <div
                key={rv.id}
                className="rounded-xl border border-slate-200 p-3 flex flex-col sm:flex-row gap-3"
                data-testid={`admin-review-${rv.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{rv.author}</span>
                    <span className="text-[11px] text-slate-500">
                      · {rv.location}
                    </span>
                    {rv.source === "ai_generated" && (
                      <span className="text-[10px] font-bold uppercase bg-sky-100 text-sky-700 rounded-full px-1.5 py-0.5">
                        AI
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 mt-1 leading-snug">
                    🇵🇱 {rv.text_pl}
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5 leading-snug">
                    🇬🇧 {rv.text_en}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(rv.id)}
                  className="self-start text-rose-600 hover:bg-rose-50 rounded-lg p-1.5"
                  title={t("admin.reviews_delete")}
                  data-testid={`admin-review-delete-${rv.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
